import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cx } from "../cx.ts";

export type ToastTone = "info" | "ok" | "danger";

export interface ToastInput {
  title: string;
  body?: string;
  tone?: ToastTone;
  /** Milliseconds before it hides itself; 0 keeps it until dismissed. Default 5,000. */
  durationMs?: number;
}

interface ToastItem extends Required<Omit<ToastInput, "body">> {
  id: number;
  body: string | undefined;
}

interface ToastApi {
  toast: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  info: <Info aria-hidden className="size-5 shrink-0" />,
  ok: <CircleCheck aria-hidden className="size-5 shrink-0 text-ok" />,
  danger: <CircleAlert aria-hidden className="size-5 shrink-0 text-danger" />,
};

/**
 * Short confirmations ("Saved", "Link copied") in a polite live region, so
 * screen readers announce them without moving focus. Errors that need a
 * decision belong in the page, not a toast.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      const item: ToastItem = {
        id,
        title: input.title,
        body: input.body,
        tone: input.tone ?? "info",
        durationMs: input.durationMs ?? 5000,
      };
      // Keep the three newest; older ones have been read or missed.
      setItems((list) => [...list.slice(-2), item]);
      if (item.durationMs > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), item.durationMs),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
    };
  }, []);

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        aria-live="polite"
        aria-label="Notifications"
        className="pointer-events-none fixed inset-x-0 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-50 flex flex-col items-center gap-2 px-4 lg:bottom-6 lg:items-end"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.tone === "danger" ? "alert" : "status"}
            className={cx(
              "sq-toast pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-surface p-3 text-ink shadow-lg",
              "sun:border-2 sun:border-black",
              t.tone === "danger" ? "border-danger" : "border-line",
            )}
          >
            {ICONS[t.tone]}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm sun:text-min">{t.title}</p>
              {t.body ? (
                <p className="mt-0.5 text-ink-muted text-sm sun:text-min">{t.body}</p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              className="-m-1 inline-flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
              onClick={() => dismiss(t.id)}
            >
              <X aria-hidden className="size-4" />
            </button>
          </div>
        ))}
      </section>
    </ToastContext.Provider>
  );
}

/** toast() and dismiss() from the nearest ToastProvider. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast must be used inside <ToastProvider>");
  return api;
}
